import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import loginDataSchema from '../schemas/loginDataSchema';
import { useLogin } from '../hooks/useLogin';
import clsx from 'clsx';
import { useCallback, useState } from 'react';

type LoginFormValues = {
    email: string;
    password: string;
};

const LoginForm: React.FC = () => {
    const [showPassword, setShowPassword] = useState(false);

    const { register, handleSubmit, formState: { errors } } = useForm<LoginFormValues>({
        resolver: zodResolver(loginDataSchema),
    });

    const { mutate, isPending } = useLogin();
    const [apiError, setApiError] = useState<string | null>(null);

    const onSubmit = useCallback((data: LoginFormValues) => {
        setApiError(null);
        mutate(data, {
            onError: (err: any) => {
                const errorMessage = err.response?.data?.message || "Something went wrong, please try again";
                setApiError(errorMessage);
            },
        });
    }, [mutate]);

    return (
        <form onSubmit={handleSubmit(onSubmit)} className='mt-4 pt-2'>
            {apiError && <div className="alert alert-important alert-danger alert-dismissible" role='alert'>{apiError}</div>}
            <div className="mb-4">
                <input className={clsx("form-control", errors.email && "is-invalid")} placeholder="Email" autoComplete="off" disabled={isPending} {...register('email')} />
                {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
            </div>
            <div className="input-group mb-4">
                <input
                    className={clsx("form-control", errors.password && "is-invalid")}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    autoComplete="off"
                    disabled={isPending}
                    {...register('password')}
                />
                <span className="input-group-text" onClick={() => setShowPassword(!showPassword)} style={{ cursor: 'pointer' }}>
                    {showPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><path d="M10.585 10.587a2 2 0 0 0 2.829 2.828"></path><path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87"></path><path d="M3 3l18 18"></path></svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0"></path><path d="M21 12c-2.4 4 -5.4 6 -9 6s-6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6s6.6 2 9 6"></path></svg>
                    )}
                </span>
                {errors.password && <div className="invalid-feedback">{errors.password.message}</div>}
            </div>

            {/* Login Button */}
            <div className="mb-3">
                <button type="submit" className="btn btn-primary w-100" disabled={isPending}>
                    {isPending ? 'Loading...' : 'Login'}
                </button>
            </div>
        </form>
    );
};

export default LoginForm;
